// tokenText.js - the pure @figure token (de)serializer for NarrativeTokenField (SP6 Task 4).
//
// A narrative field's persisted/stored value is ALWAYS a plain string that may contain
// `{{fig:KEY}}` tokens (the same tokens renderNarrative.jsx resolves for preview/print).
// NarrativeTokenField needs a structured, editable representation of that string - a list of
// "segments" it can render as text runs + atomic chips, splice into on @-insert, and delete a
// whole chip out of on backspace. parseTokens/serializeTokens are the pure, side-effect-free
// bridge between the two: the field's `value` prop and `onChange` callback only ever see the
// plain token STRING (so persistence/rendering, unaware of the editor's internals, are
// unaffected); the segment list is purely an internal editing convenience.
//
// KEY charset mirrors renderNarrative.jsx's FIG_TOKEN regex exactly (lowercase letters/digits/
// underscore) so a token this module parses is guaranteed to also be one renderNarrative renders
// live, and vice versa - the two never disagree about what counts as a well-formed token.
const FIG_TOKEN = /\{\{fig:([a-z0-9_]+)\}\}/g;

/**
 * parseTokens(str) -> segments[] - splits `str` into an ordered list of
 * `{type:'text', value}` and `{type:'fig', key}` segments. Pure, never throws.
 *
 * - null/undefined -> `[]` (no segments - an empty field has nothing to render).
 * - empty string -> `[]` (same reasoning; there's no zero-length text segment).
 * - a malformed token (missing closing `}}`, or a key outside the a-z0-9_ charset, e.g.
 *   `{{fig:Not-Valid}}`) never matches the regex, so it falls straight through as ordinary
 *   literal text - no special-casing, no crash.
 */
export function parseTokens(str) {
  if (typeof str !== "string" || str.length === 0) return [];

  const segments = [];
  let lastIndex = 0;
  let match;
  // Reset lastIndex defensively - FIG_TOKEN is a shared module-level /g regex, and a stateful
  // regex left mid-scan (e.g. by a prior throw) would otherwise corrupt the next call.
  FIG_TOKEN.lastIndex = 0;

  while ((match = FIG_TOKEN.exec(str)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: "text", value: str.slice(lastIndex, match.index) });
    }
    segments.push({ type: "fig", key: match[1] });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < str.length) {
    segments.push({ type: "text", value: str.slice(lastIndex) });
  }

  return segments;
}

/**
 * serializeTokens(segments) -> str - the inverse of parseTokens: text segments emit their
 * `value` verbatim, fig segments emit `{{fig:KEY}}`. Pure, never throws.
 *
 * `serializeTokens(parseTokens(s)) === s` holds for ANY string `s` (round-trip identity),
 * including the empty string, a token-free string, and strings with adjacent/mixed tokens -
 * this is the contract NarrativeTokenField relies on to never silently mutate a narrative it
 * merely re-renders.
 */
export function serializeTokens(segments) {
  if (!Array.isArray(segments)) return "";
  return segments
    .map((segment) => {
      if (!segment) return "";
      if (segment.type === "fig") return `{{fig:${segment.key}}}`;
      return segment.value ?? "";
    })
    .join("");
}
